import { z } from 'zod';
/**
 * Canonical model every portal recipe maps into. The server matches and writes to
 * Maximizer from these shapes only; raw portal payloads never reach the CRM writer.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
export const LineOfBusiness = z.enum([
    'Life',
    'Critical Illness',
    'Disability',
    'Health',
    'Investment',
    'Annuity',
    'Other',
]);
/** Normalized status vocabulary. The portal's own label is kept in `statusRaw`. */
export const PolicyStatus = z.enum([
    'Inforce',
    'Pending',
    'Grace',
    'Lapsed',
    'Reinstated',
    'Surrendered',
    'PaidUp',
    'Matured',
    'Terminated',
    'Unknown',
]);
export const PremiumFrequency = z.enum(['Monthly', 'Quarterly', 'SemiAnnual', 'Annual', 'Single', 'Unknown']);
export const Beneficiary = z.object({
    name: z.string(),
    relationship: z.string().nullish(),
    sharePct: z.number().nullish(),
    designation: z.string().nullish(),
    revocable: z.boolean().nullish(),
});
export const CanonicalClient = z.object({
    /** The portal's own id for this person (stable across syncs). */
    externalId: z.string(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    fullName: z.string().min(1),
    dateOfBirth: isoDate.nullish(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
    address: z
        .object({
        line1: z.string().nullish(),
        city: z.string().nullish(),
        province: z.string().nullish(),
        postalCode: z.string().nullish(),
    })
        .nullish(),
    /** The household (family) this person belongs to in the source CRM, and their place in it (Primary, Spouse, Child…). Flat so a file without them maps to nulls. */
    householdId: z.string().nullish(),
    householdName: z.string().nullish(),
    householdRole: z.string().nullish(),
});
export const CanonicalPolicy = z.object({
    externalId: z.string(),
    policyNumber: z.string().min(1),
    carrierCode: z.string().min(1),
    carrierName: z.string().nullish(),
    lineOfBusiness: LineOfBusiness,
    productType: z.string().nullish(),
    productCode: z.string().nullish(),
    planName: z.string().nullish(),
    status: PolicyStatus,
    statusRaw: z.string().nullish(),
    insuredName: z.string().nullish(),
    ownerName: z.string().nullish(),
    jointInsuredName: z.string().nullish(),
    currency: z.string().default('CAD'),
    faceAmount: z.number().nullish(),
    premiumAmount: z.number().nullish(),
    premiumFrequency: PremiumFrequency.nullish(),
    annualPremium: z.number().nullish(),
    cashValue: z.number().nullish(),
    issueDate: isoDate.nullish(),
    effectiveDate: isoDate.nullish(),
    renewalDate: isoDate.nullish(),
    terminationDate: isoDate.nullish(),
    paidToDate: isoDate.nullish(),
    maturityDate: isoDate.nullish(),
    beneficiaries: z.array(Beneficiary).default([]),
});
/** One captured unit after normalization: a person and the policies under them. */
export const ClientBundle = z.object({
    client: CanonicalClient,
    policies: z.array(CanonicalPolicy),
});
/** Natural key used for idempotent upserts into the CRM. */
export function policyNaturalKey(p) {
    return `${p.carrierCode.trim().toUpperCase()}::${p.policyNumber.trim().toUpperCase()}`;
}
//# sourceMappingURL=policy.js.map